import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Table, Spinner, Alert, Form, Button, ButtonGroup } from "react-bootstrap";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, LineElement, PointElement, Filler } from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import api from "../../services/api";
import Template from "../../template";
import { toast } from "react-toastify";
import { useAuth } from "../../context/auth";
import * as S from "./styled";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
);

interface VendaData {
  nome: string;
  plano: string;
  forma_pagamento: string;
  created_at: string;
  email: string;
  telefone: string;
}

interface DesistenciaData {
  nome: string;
  plano: string;
  forma_pagamento: string;
  created_at: string;
  email: string;
  telefone: string;
}

interface RecusadoData {
  nome: string;
  plano: string;
  forma_pagamento: string;
  created_at: string;
  email: string;
  telefone: string;
}

interface TestaramData {
  nome: string;
  email: string;
  telefone: string;
  testou: boolean | number; // Pode ser boolean ou number (0/1)
  created_at: string;
}

interface AlunoPresencialData {
  nome: string;
  email: string;
  telefone: string;
  created_at: string;
}

interface AlunosPresenciaisResponse {
  data: AlunoPresencialData[];
}

interface RelatorioTesteResponse {
  data: TestaramData[];
}

interface RelatorioData {
  vendas: {
    quantidade: number;
    data: VendaData[];
  };
  recusados: {
    quantidade: number;
    data: RecusadoData[];
  };
  desistencias: {
    quantidade: number;
    data: DesistenciaData[];
  };
  testaram: {
    quantidade: number;
    data: TestaramData[];
  };
}

interface RelatorioResponse {
  infos: RelatorioData;
}

function Relatorios() {
  const { user }: any = useAuth();
  const [relatorio, setRelatorio] = useState<RelatorioData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para filtros
  const [filtroPlano, setFiltroPlano] = useState<string>("");
  const [filtroFormaPagamento, setFiltroFormaPagamento] = useState<string>("");
  const [filtroDataInicio, setFiltroDataInicio] = useState<string>("");
  const [filtroDataFim, setFiltroDataFim] = useState<string>("");
  
  // Estados para tipos de gráfico
  const [tipoGraficoVendas, setTipoGraficoVendas] = useState<string>("doughnut");
  const [tipoGraficoDesistencias, setTipoGraficoDesistencias] = useState<string>("doughnut");
  
  // Estado para gráfico temporal
  const [periodoTemporal, setPeriodoTemporal] = useState<string>("diario");

  // Estados para paginação das tabelas
  const [paginaVendas, setPaginaVendas] = useState<number>(1);
  const [paginaRecusados, setPaginaRecusados] = useState<number>(1);
  const [paginaDesistencias, setPaginaDesistencias] = useState<number>(1);
  const [paginaTestaram, setPaginaTestaram] = useState<number>(1);
  const [itensPorPagina] = useState<number>(20);

  // Estados para filtros das tabelas
  const [filtroPlanoVendas, setFiltroPlanoVendas] = useState<string>("");
  const [filtroFormaPagamentoVendas, setFiltroFormaPagamentoVendas] = useState<string>("");
  const [filtroDataInicioVendas, setFiltroDataInicioVendas] = useState<string>("");
  const [filtroDataFimVendas, setFiltroDataFimVendas] = useState<string>("");
  
  const [filtroPlanoDesistencias, setFiltroPlanoDesistencias] = useState<string>("");
  const [filtroFormaPagamentoDesistencias, setFiltroFormaPagamentoDesistencias] = useState<string>("");
  const [filtroDataInicioDesistencias, setFiltroDataInicioDesistencias] = useState<string>("");
  const [filtroDataFimDesistencias, setFiltroDataFimDesistencias] = useState<string>("");

  const [filtroPlanoRecusados, setFiltroPlanoRecusados] = useState<string>("");
  const [filtroFormaPagamentoRecusados, setFiltroFormaPagamentoRecusados] = useState<string>("");
  const [filtroDataInicioRecusados, setFiltroDataInicioRecusados] = useState<string>("");
  const [filtroDataFimRecusados, setFiltroDataFimRecusados] = useState<string>("");

  const [filtroDataInicioTestaram, setFiltroDataInicioTestaram] = useState<string>("");
  const [filtroDataFimTestaram, setFiltroDataFimTestaram] = useState<string>("");
  const [filtroTestou, setFiltroTestou] = useState<string>(""); // Novo filtro para testou/não testou

  // Estados para alunos presenciais
  const [alunosPresenciais, setAlunosPresenciais] = useState<AlunoPresencialData[]>([]);
  const [loadingPresenciais, setLoadingPresenciais] = useState<boolean>(false);
  const [paginaPresenciais, setPaginaPresenciais] = useState<number>(1);
  const [filtroDataInicioPresenciais, setFiltroDataInicioPresenciais] = useState<string>("");
  const [filtroDataFimPresenciais, setFiltroDataFimPresenciais] = useState<string>("");

  // Estados para dados de teste
  const [dadosTestaram, setDadosTestaram] = useState<TestaramData[]>([]);
  const [loadingTestaram, setLoadingTestaram] = useState<boolean>(false);

  // Verificar se o usuário é admin e tem ID = 413 ou 1668
  useEffect(() => {
    if (user && (user.nivel !== "a" || (user.id !== 413 && user.id !== 1668))) {
      toast.error("Acesso negado. Apenas administradores autorizados podem acessar relatórios.");
      return;
    }
  }, [user]);

  const buscarRelatorio = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get<RelatorioResponse>("/relatorio");
      
      if (response.data && response.data.infos) {
        setRelatorio(response.data.infos);
      } else {
        setError("Formato de resposta inválido");
      }
    } catch (error: any) {
      console.error("Erro ao buscar relatório:", error);
      
      if (error.response?.status === 500) {
        setError("Erro interno do servidor");
        toast.error("Erro interno do servidor");
      } else {
        setError("Erro ao carregar relatório");
        toast.error("Erro ao carregar relatório");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.nivel === "a" && (user.id === 413 || user.id === 1668)) {
      buscarRelatorio();
      buscarAlunosPresenciais();
      buscarDadosTestaram();
    }
  }, [user]);

  const buscarAlunosPresenciais = async () => {
    try {
      setLoadingPresenciais(true);
      
      const response = await api.get<AlunosPresenciaisResponse>("/users/presencial");
      
      if (response.data && response.data.data) {
        setAlunosPresenciais(response.data.data);
      } else {
        setAlunosPresenciais([]);
      }
    } catch (error: any) {
      console.error("Erro ao buscar alunos presenciais:", error);
      
      if (error.response?.status === 500) {
        toast.error("Erro interno do servidor ao buscar alunos presenciais");
      } else {
        toast.error("Erro ao carregar alunos presenciais");
      }
      setAlunosPresenciais([]);
    } finally {
      setLoadingPresenciais(false);
    }
  };

  const buscarDadosTestaram = async () => {
    try {
      setLoadingTestaram(true);
      
      const response = await api.get<RelatorioTesteResponse>("/relatorio-teste");
      
      if (response.data && response.data.data) {
        setDadosTestaram(response.data.data);
      } else {
        setDadosTestaram([]);
      }
    } catch (error: any) {
      console.error("Erro ao buscar dados de testaram:", error);
      
      if (error.response?.status === 500) {
        toast.error("Erro interno do servidor ao buscar dados de testaram");
      } else {
        toast.error("Erro ao carregar dados de testaram");
      }
      setDadosTestaram([]);
    } finally {
      setLoadingTestaram(false);
    }
  };

  const formatarData = (dataString: string) => {
    try {
      const data = new Date(dataString);
      return data.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dataString;
    }
  };

  // Função para filtrar dados de vendas
  const filtrarVendas = () => {
    if (!relatorio) return [];
    
    return relatorio.vendas.data.filter(venda => {
      const matchPlano = !filtroPlanoVendas || venda.plano === filtroPlanoVendas;
      const matchFormaPagamento = !filtroFormaPagamentoVendas || venda.forma_pagamento === filtroFormaPagamentoVendas;
      
      // Filtro por intervalo de datas
      let matchData = true;
      if (filtroDataInicioVendas || filtroDataFimVendas) {
        const dataVenda = new Date(venda.created_at);
        if (filtroDataInicioVendas) {
          const dataInicio = new Date(filtroDataInicioVendas);
          matchData = matchData && dataVenda >= dataInicio;
        }
        if (filtroDataFimVendas) {
          const dataFim = new Date(filtroDataFimVendas);
          dataFim.setHours(23, 59, 59, 999); // Incluir o dia inteiro
          matchData = matchData && dataVenda <= dataFim;
        }
      }
      
      return matchPlano && matchFormaPagamento && matchData;
    });
  };

  // Função para filtrar dados de desistências
  const filtrarDesistencias = () => {
    if (!relatorio) return [];
    
    return relatorio.desistencias.data.filter(desistencia => {
      const matchPlano = !filtroPlanoDesistencias || desistencia.plano === filtroPlanoDesistencias;
      const matchFormaPagamento = !filtroFormaPagamentoDesistencias || desistencia.forma_pagamento === filtroFormaPagamentoDesistencias;
      
      // Filtro por intervalo de datas
      let matchData = true;
      if (filtroDataInicioDesistencias || filtroDataFimDesistencias) {
        const dataDesistencia = new Date(desistencia.created_at);
        if (filtroDataInicioDesistencias) {
          const dataInicio = new Date(filtroDataInicioDesistencias);
          matchData = matchData && dataDesistencia >= dataInicio;
        }
        if (filtroDataFimDesistencias) {
          const dataFim = new Date(filtroDataFimDesistencias);
          dataFim.setHours(23, 59, 59, 999); // Incluir o dia inteiro
          matchData = matchData && dataDesistencia <= dataFim;
        }
      }
      
      return matchPlano && matchFormaPagamento && matchData;
    });
  };

  // Função para filtrar dados de recusados
  const filtrarRecusados = () => {
    if (!relatorio) return [];

    return relatorio.recusados.data.filter(recusado => {
      const matchPlano = !filtroPlanoRecusados || recusado.plano === filtroPlanoRecusados;
      const matchFormaPagamento = !filtroFormaPagamentoRecusados || recusado.forma_pagamento === filtroFormaPagamentoRecusados;

      // Filtro por intervalo de datas
      let matchData = true;
      if (filtroDataInicioRecusados || filtroDataFimRecusados) {
        const dataRecusado = new Date(recusado.created_at);
        if (filtroDataInicioRecusados) {
          const dataInicio = new Date(filtroDataInicioRecusados);
          matchData = matchData && dataRecusado >= dataInicio;
        }
        if (filtroDataFimRecusados) {
          const dataFim = new Date(filtroDataFimRecusados);
          dataFim.setHours(23, 59, 59, 999); // Incluir o dia inteiro
          matchData = matchData && dataRecusado <= dataFim;
        }
      }

      return matchPlano && matchFormaPagamento && matchData;
    });
  };

  // Função para filtrar dados de testaram
  const filtrarTestaram = () => {
    return dadosTestaram.filter(testaram => {
      // Filtro por intervalo de datas
      let matchData = true;
      if (filtroDataInicioTestaram || filtroDataFimTestaram) {
        const dataTestaram = new Date(testaram.created_at);
        if (filtroDataInicioTestaram) {
          const dataInicio = new Date(filtroDataInicioTestaram);
          matchData = matchData && dataTestaram >= dataInicio;
        }
        if (filtroDataFimTestaram) {
          const dataFim = new Date(filtroDataFimTestaram);
          dataFim.setHours(23, 59, 59, 999); // Incluir o dia inteiro
          matchData = matchData && dataTestaram <= dataFim;
        }
      }
      
      // Filtro por testou/não testou
      let matchTestou = true;
      if (filtroTestou) {
        if (filtroTestou === "sim") {
          matchTestou = testaram.testou === 1 || testaram.testou === true;
        } else if (filtroTestou === "nao") {
          matchTestou = testaram.testou === 0 || testaram.testou === false;
        }
      }
      
      return matchData && matchTestou;
    });
  };

  // Função para filtrar dados de alunos presenciais
  const filtrarPresenciais = () => {
    return alunosPresenciais.filter(aluno => {
      // Filtro por intervalo de datas
      let matchData = true;
      if (filtroDataInicioPresenciais || filtroDataFimPresenciais) {
        const dataAluno = new Date(aluno.created_at);
        if (filtroDataInicioPresenciais) {
          const dataInicio = new Date(filtroDataInicioPresenciais);
          matchData = matchData && dataAluno >= dataInicio;
        }
        if (filtroDataFimPresenciais) {
          const dataFim = new Date(filtroDataFimPresenciais);
          dataFim.setHours(23, 59, 59, 999); // Incluir o dia inteiro
          matchData = matchData && dataAluno <= dataFim;
        }
      }
      
      return matchData;
    });
  };

  // Função para paginar dados
  const paginarDados = (dados: any[], pagina: number) => {
    const inicio = (pagina - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return dados.slice(inicio, fim);
  };

  // Calcular dados filtrados e paginados
  const vendasFiltradas = filtrarVendas();
  const recusadosFiltradas = filtrarRecusados();
  const desistenciasFiltradas = filtrarDesistencias();
  const testaramFiltradas = filtrarTestaram();
  const presenciaisFiltradas = filtrarPresenciais();
  const vendasPaginadas = paginarDados(vendasFiltradas, paginaVendas);
  const recusadosPaginadas = paginarDados(recusadosFiltradas, paginaRecusados);
  const desistenciasPaginadas = paginarDados(desistenciasFiltradas, paginaDesistencias);
  const testaramPaginadas = paginarDados(testaramFiltradas, paginaTestaram);
  const presenciaisPaginadas = paginarDados(presenciaisFiltradas, paginaPresenciais);

  // Calcular total de páginas
  const totalPaginasVendas = Math.ceil(vendasFiltradas.length / itensPorPagina);
  const totalPaginasRecusados = Math.ceil(recusadosFiltradas.length / itensPorPagina);
  const totalPaginasDesistencias = Math.ceil(desistenciasFiltradas.length / itensPorPagina);
  const totalPaginasTestaram = Math.ceil(testaramFiltradas.length / itensPorPagina);
  const totalPaginasPresenciais = Math.ceil(presenciaisFiltradas.length / itensPorPagina);

  const formatarFormaPagamento = (forma: string) => {
    const formas: { [key: string]: string } = {
      "PIX": "PIX",
      "CREDIT_CARD": "Cartão de Crédito",
      "DEBIT_CARD": "Cartão de Débito",
      "BOLETO": "Boleto"
    };
    return formas[forma] || forma;
  };

  const formatarTelefone = (telefone: string) => {
    if (!telefone) return 'N/A';
    
    // Remove todos os caracteres não numéricos
    const numeroLimpo = telefone.replace(/\D/g, '');
    
    // Se não tem 10 ou 11 dígitos, retorna como está
    if (numeroLimpo.length < 10 || numeroLimpo.length > 11) {
      return telefone;
    }
    
    // Formatar para (99) 99999-9999 ou (99) 9999-9999
    if (numeroLimpo.length === 11) {
      return `(${numeroLimpo.slice(0, 2)}) ${numeroLimpo.slice(2, 7)}-${numeroLimpo.slice(7)}`;
    } else {
      return `(${numeroLimpo.slice(0, 2)}) ${numeroLimpo.slice(2, 6)}-${numeroLimpo.slice(6)}`;
    }
  };

  // Função para exportar dados de vendas para XLSX
  const exportarVendasXLSX = () => {
    const dadosFiltrados = filtrarDados(relatorio?.vendas.data || []);
    
    const dadosParaExportar = dadosFiltrados.map(venda => ({
      'Plano': venda.plano,
      'Forma de Pagamento': formatarFormaPagamento(venda.forma_pagamento),
      'Email': venda.email,
      'Telefone': formatarTelefone(venda.telefone),
      'Data': formatarData(venda.created_at)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendas Aprovadas');
    
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(workbook, `vendas-aprovadas-${dataAtual}.xlsx`);
  };

  // Função para exportar dados de recusados para XLSX
  const exportarRecusadosXLSX = () => {
    const dadosFiltrados = filtrarDados(relatorio?.recusados.data || []);

    const dadosParaExportar = dadosFiltrados.map(recusado => ({
      'Plano': recusado.plano,
      'Forma de Pagamento': formatarFormaPagamento(recusado.forma_pagamento),
      'Email': recusado.email,
      'Telefone': formatarTelefone(recusado.telefone),
      'Data': formatarData(recusado.created_at)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Recusados');

    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(workbook, `recusados-${dataAtual}.xlsx`);
  };

  // Função para exportar dados de desistências para XLSX
  const exportarDesistenciasXLSX = () => {
    const dadosFiltrados = filtrarDados(relatorio?.desistencias.data || []);
    
    const dadosParaExportar = dadosFiltrados.map(desistencia => ({
      'Plano': desistencia.plano,
      'Forma de Pagamento': formatarFormaPagamento(desistencia.forma_pagamento),
      'Email': desistencia.email,
      'Telefone': formatarTelefone(desistencia.telefone),
      'Data': formatarData(desistencia.created_at)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Desistências');
    
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(workbook, `desistencias-${dataAtual}.xlsx`);
  };

  // Função para exportar dados de testaram para XLSX
  const exportarTestaramXLSX = () => {
    const dadosFiltrados = filtrarTestaram();
    
    const dadosParaExportar = dadosFiltrados.map(testaram => ({
      'Nome': testaram.nome,
      'Email': testaram.email,
      'Telefone': formatarTelefone(testaram.telefone),
      'Enviou uma redação': testaram.testou ? 'Sim' : 'Não',
      'Data': formatarData(testaram.created_at)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Testaram');
    
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(workbook, `testaram-${dataAtual}.xlsx`);
  };

  // Função para exportar dados de alunos presenciais para XLSX
  const exportarPresenciaisXLSX = () => {
    const dadosFiltrados = filtrarPresenciais();
    
    const dadosParaExportar = dadosFiltrados.map(aluno => ({
      'Nome': aluno.nome,
      'Email': aluno.email,
      'Telefone': formatarTelefone(aluno.telefone),
      'Data de Cadastro': formatarData(aluno.created_at)
    }));

    const worksheet = XLSX.utils.json_to_sheet(dadosParaExportar);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Alunos Presenciais');
    
    const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    XLSX.writeFile(workbook, `alunos-presenciais-${dataAtual}.xlsx`);
  };

  // Função para filtrar dados baseado nos filtros selecionados
  const filtrarDados = (dados: VendaData[] | DesistenciaData[] | RecusadoData[]) => {
    return dados.filter(item => {
      // Filtro por plano
      if (filtroPlano && item.plano !== filtroPlano) return false;
      
      // Filtro por forma de pagamento
      if (filtroFormaPagamento && item.forma_pagamento !== filtroFormaPagamento) return false;
      
      // Filtro por data
      if (filtroDataInicio || filtroDataFim) {
        const dataItem = new Date(item.created_at);
        if (filtroDataInicio && dataItem < new Date(filtroDataInicio)) return false;
        if (filtroDataFim && dataItem > new Date(filtroDataFim + 'T23:59:59')) return false;
      }
      
      return true;
    });
  };

  // Função para obter listas únicas para os filtros
  const obterOpcoesUnicas = () => {
    if (!relatorio) return { planos: [], formasPagamento: [] };
    
    const todosItens = [...relatorio.vendas.data, ...relatorio.recusados.data, ...relatorio.desistencias.data];
    
    const planos = Array.from(new Set(todosItens.map(item => item.plano)));
    const formasPagamento = Array.from(new Set(todosItens.map(item => item.forma_pagamento)));
    
    return { planos, formasPagamento };
  };

  // Função para processar dados temporais
  const processarDadosTemporais = () => {
    if (!relatorio) return null;

    // Aplicar filtros aos dados
    const vendasFiltradas = filtrarDados(relatorio.vendas.data);
    const recusadosFiltradas = filtrarDados(relatorio.recusados.data);
    const desistenciasFiltradas = filtrarDados(relatorio.desistencias.data);

    // Função para agrupar dados por período
    const agruparPorPeriodo = (dados: (VendaData | DesistenciaData | RecusadoData)[]) => {
      const agrupados: { [key: string]: number } = {};

      dados.forEach(item => {
        const data = new Date(item.created_at);
        let chave: string;

        switch (periodoTemporal) {
          case 'diario':
            chave = data.toISOString().split('T')[0]; // YYYY-MM-DD
            break;
          case 'semanal':
            const inicioSemana = new Date(data);
            inicioSemana.setDate(data.getDate() - data.getDay());
            chave = inicioSemana.toISOString().split('T')[0];
            break;
          case 'mensal':
            chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
            break;
          default:
            chave = data.toISOString().split('T')[0];
        }

        agrupados[chave] = (agrupados[chave] || 0) + 1;
      });

      return agrupados;
    };

    const vendasAgrupadas = agruparPorPeriodo(vendasFiltradas);
    const recusadosAgrupadas = agruparPorPeriodo(recusadosFiltradas);
    const desistenciasAgrupadas = agruparPorPeriodo(desistenciasFiltradas);

    // Obter todas as datas únicas e ordená-las
    const todasDatas = Array.from(new Set([
      ...Object.keys(vendasAgrupadas),
      ...Object.keys(recusadosAgrupadas),
      ...Object.keys(desistenciasAgrupadas)
    ])).sort();

    // Formatar labels baseado no período
    const formatarLabel = (data: string) => {
      switch (periodoTemporal) {
        case 'diario':
          return new Date(data).toLocaleDateString('pt-BR');
        case 'semanal':
          const inicioSemana = new Date(data);
          const fimSemana = new Date(inicioSemana);
          fimSemana.setDate(inicioSemana.getDate() + 6);
          return `${inicioSemana.toLocaleDateString('pt-BR')} - ${fimSemana.toLocaleDateString('pt-BR')}`;
        case 'mensal':
          const [ano, mes] = data.split('-');
          const nomesMeses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
          return `${nomesMeses[parseInt(mes) - 1]} ${ano}`;
        default:
          return data;
      }
    };

    const labels = todasDatas.map(formatarLabel);
    const dadosVendas = todasDatas.map(data => vendasAgrupadas[data] || 0);
    const dadosRecusados = todasDatas.map(data => recusadosAgrupadas[data] || 0);
    const dadosDesistencias = todasDatas.map(data => desistenciasAgrupadas[data] || 0);

    return {
      labels,
      datasets: [
        {
          label: 'Vendas',
          data: dadosVendas,
          borderColor: 'rgb(40, 167, 69)',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          tension: 0.1,
          fill: true
        },
        {
          label: 'Recusados',
          data: dadosRecusados,
          borderColor: 'rgb(220, 53, 69)',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          tension: 0.1,
          fill: true
        },
        {
          label: 'Desistências',
          data: dadosDesistencias,
          borderColor: 'rgb(255, 193, 7)',
          backgroundColor: 'rgba(255, 193, 7, 0.1)',
          tension: 0.1,
          fill: true
        }
      ]
    };
  };

  // Função para processar dados dos gráficos com filtros aplicados
  const processarDadosGraficos = () => {
    if (!relatorio) return { vendasChart: null, desistenciasChart: null };

    // Aplicar filtros aos dados
    const vendasFiltradas = filtrarDados(relatorio.vendas.data);
    const desistenciasFiltradas = filtrarDados(relatorio.desistencias.data);

    // Processar dados de vendas por plano
    const vendasPorPlano = vendasFiltradas.reduce((acc: { [key: string]: number }, venda) => {
      acc[venda.plano] = (acc[venda.plano] || 0) + 1;
      return acc;
    }, {});

    // Processar dados de desistências por plano
    const desistenciasPorPlano = desistenciasFiltradas.reduce((acc: { [key: string]: number }, desistencia) => {
      acc[desistencia.plano] = (acc[desistencia.plano] || 0) + 1;
      return acc;
    }, {});

    // Dados para gráfico de vendas
    const vendasChart = {
      labels: Object.keys(vendasPorPlano),
      datasets: [
        {
          label: 'Vendas por Plano',
          data: Object.values(vendasPorPlano),
          backgroundColor: [
            'rgba(40, 167, 69, 0.8)',
            'rgba(23, 162, 184, 0.8)',
            'rgba(255, 193, 7, 0.8)',
            'rgba(220, 53, 69, 0.8)',
            'rgba(108, 117, 125, 0.8)'
          ],
          borderColor: [
            'rgba(40, 167, 69, 1)',
            'rgba(23, 162, 184, 1)',
            'rgba(255, 193, 7, 1)',
            'rgba(220, 53, 69, 1)',
            'rgba(108, 117, 125, 1)'
          ],
          borderWidth: 1,
          fill: tipoGraficoVendas === 'area'
        }
      ]
    };

    // Dados para gráfico de desistências
    const desistenciasChart = {
      labels: Object.keys(desistenciasPorPlano),
      datasets: [
        {
          label: 'Desistências por Plano',
          data: Object.values(desistenciasPorPlano),
          backgroundColor: [
            'rgba(255, 193, 7, 0.8)',
            'rgba(220, 53, 69, 0.8)',
            'rgba(108, 117, 125, 0.8)',
            'rgba(255, 87, 34, 0.8)',
            'rgba(156, 39, 176, 0.8)'
          ],
          borderColor: [
            'rgba(255, 193, 7, 1)',
            'rgba(220, 53, 69, 1)',
            'rgba(108, 117, 125, 1)',
            'rgba(255, 87, 34, 1)',
            'rgba(156, 39, 176, 1)'
          ],
          borderWidth: 1,
          fill: tipoGraficoDesistencias === 'area'
        }
      ]
    };

    return { vendasChart, desistenciasChart };
  };

  const { vendasChart, desistenciasChart } = processarDadosGraficos();

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  // Não renderizar se não for admin com ID = 413 ou 1668
  if (user && (user.nivel !== "a" || (user.id !== 413 && user.id !== 1668))) {
    return (
      <Template>
        <Container fluid className="pt-4 pb-4">
          <Alert variant="danger">
            <Alert.Heading>Acesso Negado</Alert.Heading>
            <p>Apenas administradores autorizados podem acessar esta página.</p>
          </Alert>
        </Container>
      </Template>
    );
  }

  return (
    <Template>
      <S.MainWrapper>
        <Container fluid className="pt-4 pb-4" style={{ maxWidth: '95%' }}>
          <Row>
            <Col sm={12}>
              {/* Header */}
              <div className="shadow-sm p-4 mb-4 bg-white rounded">
                <Row>
                  <Col md={8}>
                    <div className="d-flex align-items-center">
                      <div className="bg-primary text-white rounded-circle p-3 mr-3">
                        <i className="fas fa-chart-bar fa-2x"></i>
                      </div>
                      <div>
                        <h4 className="mb-1 font-weight-bold text-dark">Relatórios</h4>
                        <p className="mb-0 text-muted">Relatório de vendas, recusados e desistências</p>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-right">
                      <button
                        className="btn btn-primary"
                        onClick={buscarRelatorio}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="mr-2"
                            />
                            Carregando...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-sync-alt mr-2"></i>
                            Atualizar
                          </>
                        )}
                      </button>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Conteúdo */}
              {error && (
                <Alert variant="danger" className="mb-4">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  {error}
                </Alert>
              )}

              {loading && !relatorio && (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Carregando relatório...</p>
                </div>
              )}

              {relatorio && (
                <>
                  {/* Controles de Filtro */}
                  <Card className="mb-4 shadow-sm">
                    <Card.Header className="bg-primary">
                      <h5 className="mb-0">
                        <i className="fas fa-filter mr-2"></i>
                        Filtros
                      </h5>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Plano</Form.Label>
                            <Form.Control
                              as="select"
                              value={filtroPlano}
                              onChange={(e) => setFiltroPlano(e.target.value)}
                            >
                              <option value="">Todos os planos</option>
                              {obterOpcoesUnicas().planos.map(plano => (
                                <option key={plano} value={plano}>{plano}</option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Forma de Pagamento</Form.Label>
                            <Form.Control
                              as="select"
                              value={filtroFormaPagamento}
                              onChange={(e) => setFiltroFormaPagamento(e.target.value)}
                            >
                              <option value="">Todas as formas de pagamento</option>
                              {obterOpcoesUnicas().formasPagamento.map(forma => (
                                <option key={forma} value={forma}>{formatarFormaPagamento(forma)}</option>
                              ))}
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Data Início</Form.Label>
                            <Form.Control
                              type="date"
                              value={filtroDataInicio}
                              onChange={(e) => setFiltroDataInicio(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Data Fim</Form.Label>
                            <Form.Control
                              type="date"
                              value={filtroDataFim}
                              onChange={(e) => setFiltroDataFim(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => {
                              setFiltroPlano("");
                              setFiltroFormaPagamento("");
                              setFiltroDataInicio("");
                              setFiltroDataFim("");
                            }}
                          >
                            <i className="fas fa-times mr-1"></i>
                            Limpar Filtros
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Cards de Resumo */}
                  <Row className="mb-4">
                    <Col>
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex align-items-center">
                            <div className="bg-success text-white rounded-circle p-3 mr-3">
                              <i className="fas fa-chart-line fa-2x"></i>
                            </div>
                            <div>
                              <h5 className="card-title text-success mb-1">Vendas</h5>
                              <h2 className="mb-0 font-weight-bold text-dark">{relatorio.vendas.quantidade}</h2>
                              <small className="text-muted">Total de vendas aprovadas</small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex align-items-center">
                            <div className="bg-danger text-white rounded-circle p-3 mr-3">
                              <i className="fas fa-ban fa-2x"></i>
                            </div>
                            <div>
                              <h5 className="card-title text-danger mb-1">Recusados</h5>
                              <h2 className="mb-0 font-weight-bold text-dark">{relatorio.recusados.quantidade}</h2>
                              <small className="text-muted">Total de pagamentos recusados</small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex align-items-center">
                            <div className="bg-warning text-white rounded-circle p-3 mr-3">
                              <i className="fas fa-times-circle fa-2x"></i>
                            </div>
                            <div>
                              <h5 className="card-title text-warning mb-1">Desistências</h5>
                              <h2 className="mb-0 font-weight-bold text-dark">{relatorio.desistencias.quantidade}</h2>
                              <small className="text-muted">Total de desistências</small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex align-items-center">
                            <div className="bg-info text-white rounded-circle p-3 mr-3">
                              <i className="fas fa-user-check fa-2x"></i>
                            </div>
                            <div>
                              <h5 className="card-title text-info mb-1">Usuários que enviaram redação de teste</h5>
                              <h2 className="mb-0 font-weight-bold text-dark">{relatorio.testaram.quantidade}</h2>
                              <small className="text-muted">Total de usuários que enviaram redação de teste</small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="h-100 shadow-sm">
                        <Card.Body>
                          <div className="d-flex align-items-center">
                            <div className="bg-primary text-white rounded-circle p-3 mr-3">
                              <i className="fas fa-users fa-2x"></i>
                            </div>
                            <div>
                              <h5 className="card-title text-primary mb-1">Presenciais</h5>
                              <h2 className="mb-0 font-weight-bold text-dark">{alunosPresenciais.length}</h2>
                              <small className="text-muted">Total de alunos presenciais</small>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Gráficos */}
                  <Row className="mb-4">
                    <Col md={6}>
                      <Card className="shadow-sm h-100">
                        <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">
                            <i className="fas fa-chart-bar mr-2"></i>
                            Gráfico de Vendas por Plano
                          </h6>
                          <ButtonGroup size="sm">
                            <Button
                              variant={tipoGraficoVendas === 'doughnut' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoVendas('doughnut')}
                              title="Gráfico de Rosca"
                            >
                              <i className="fas fa-chart-pie"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoVendas === 'pie' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoVendas('pie')}
                              title="Gráfico de Pizza"
                            >
                              <i className="fas fa-chart-pie"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoVendas === 'bar' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoVendas('bar')}
                              title="Gráfico de Barras"
                            >
                              <i className="fas fa-chart-bar"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoVendas === 'line' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoVendas('line')}
                              title="Gráfico de Linha"
                            >
                              <i className="fas fa-chart-line"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoVendas === 'area' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoVendas('area')}
                              title="Gráfico de Área"
                            >
                              <i className="fas fa-chart-area"></i>
                            </Button>
                          </ButtonGroup>
                        </Card.Header>
                        <Card.Body>
                          {vendasChart && vendasChart.labels.length > 0 ? (
                            <div style={{ height: '300px' }}>
                              {tipoGraficoVendas === 'doughnut' && (
                                <Doughnut 
                                  data={vendasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'bottom' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                              {tipoGraficoVendas === 'pie' && (
                                <Pie 
                                  data={vendasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'bottom' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                              {tipoGraficoVendas === 'bar' && (
                                <Bar 
                                  data={vendasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'top' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                              {(tipoGraficoVendas === 'line' || tipoGraficoVendas === 'area') && (
                                <Line 
                                  data={vendasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'top' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-chart-bar fa-3x mb-3"></i>
                              <p>Nenhum dado para exibir</p>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="shadow-sm h-100">
                        <Card.Header className="bg-warning text-white d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">
                            <i className="fas fa-chart-pie mr-2"></i>
                            Gráfico de Desistências por Plano
                          </h6>
                          <ButtonGroup size="sm">
                            <Button
                              variant={tipoGraficoDesistencias === 'doughnut' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoDesistencias('doughnut')}
                              title="Gráfico de Rosca"
                            >
                              <i className="fas fa-chart-pie"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoDesistencias === 'pie' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoDesistencias('pie')}
                              title="Gráfico de Pizza"
                            >
                              <i className="fas fa-chart-pie"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoDesistencias === 'bar' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoDesistencias('bar')}
                              title="Gráfico de Barras"
                            >
                              <i className="fas fa-chart-bar"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoDesistencias === 'line' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoDesistencias('line')}
                              title="Gráfico de Linha"
                            >
                              <i className="fas fa-chart-line"></i>
                            </Button>
                            <Button
                              variant={tipoGraficoDesistencias === 'area' ? 'light' : 'outline-light'}
                              onClick={() => setTipoGraficoDesistencias('area')}
                              title="Gráfico de Área"
                            >
                              <i className="fas fa-chart-area"></i>
                            </Button>
                          </ButtonGroup>
                        </Card.Header>
                        <Card.Body>
                          {desistenciasChart && desistenciasChart.labels.length > 0 ? (
                            <div style={{ height: '300px' }}>
                              {tipoGraficoDesistencias === 'doughnut' && (
                                <Doughnut 
                                  data={desistenciasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'bottom' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                              {tipoGraficoDesistencias === 'pie' && (
                                <Pie 
                                  data={desistenciasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'bottom' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                              {tipoGraficoDesistencias === 'bar' && (
                                <Bar 
                                  data={desistenciasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'top' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                              {(tipoGraficoDesistencias === 'line' || tipoGraficoDesistencias === 'area') && (
                                <Line 
                                  data={desistenciasChart} 
                                  options={{
                                    ...chartOptions,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      ...chartOptions.plugins,
                                      legend: {
                                        position: 'top' as const,
                                      }
                                    }
                                  }} 
                                />
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-chart-pie fa-3x mb-3"></i>
                              <p>Nenhum dado para exibir</p>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Gráfico Temporal */}
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card className="shadow-sm">
                        <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">
                            <i className="fas fa-chart-line mr-2"></i>
                            Evolução Temporal - Vendas, Recusados e Desistências
                          </h6>
                          <Form.Control
                            as="select"
                            value={periodoTemporal}
                            onChange={(e) => setPeriodoTemporal(e.target.value)}
                            style={{ width: 'auto', backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                            className="text-white"
                          >
                            <option value="diario" style={{ color: 'black' }}>Diário</option>
                            <option value="semanal" style={{ color: 'black' }}>Semanal</option>
                            <option value="mensal" style={{ color: 'black' }}>Mensal</option>
                          </Form.Control>
                        </Card.Header>
                        <Card.Body>
                          {(() => {
                            const dadosTemporais = processarDadosTemporais();
                            return dadosTemporais && dadosTemporais.labels.length > 0 ? (
                              <div style={{ height: '400px' }}>
                                <Line 
                                  data={dadosTemporais} 
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                      title: {
                                        display: true,
                                        text: `Evolução ${periodoTemporal === 'diario' ? 'Diária' : periodoTemporal === 'semanal' ? 'Semanal' : 'Mensal'} de Vendas, Recusados e Desistências`
                                      },
                                      legend: {
                                        position: 'top' as const,
                                      },
                                      tooltip: {
                                        mode: 'index' as const,
                                        intersect: false,
                                      }
                                    },
                                    scales: {
                                      x: {
                                        display: true,
                                        title: {
                                          display: true,
                                          text: 'Período'
                                        }
                                      },
                                      y: {
                                        display: true,
                                        title: {
                                          display: true,
                                          text: 'Quantidade'
                                        },
                                        beginAtZero: true
                                      }
                                    },
                                    interaction: {
                                      mode: 'nearest' as const,
                                      axis: 'x' as const,
                                      intersect: false
                                    }
                                  }} 
                                />
                              </div>
                            ) : (
                              <div className="text-center py-4 text-muted">
                                <i className="fas fa-chart-line fa-3x mb-3"></i>
                                <p>Nenhum dado temporal para exibir</p>
                              </div>
                            );
                          })()}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Tabelas lado a lado */}
                  <Row className="mb-4">
                    <Col md={6}>
                      {/* Tabela de Vendas */}
                      <Card className="shadow-sm mb-4 h-100">
                        <Card.Header className="bg-success text-white">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                              <i className="fas fa-check-circle mr-2"></i>
                              Vendas Aprovadas ({relatorio.vendas.quantidade})
                            </h5>
                            <Button 
                              variant="outline-light" 
                              size="sm" 
                              onClick={exportarVendasXLSX}
                              title="Exportar para Excel"
                            >
                              <i className="fas fa-download mr-1"></i>
                              Exportar XLSX
                            </Button>
                          </div>
                          {/* Filtros da Tabela de Vendas */}
                          {/* Primeira linha: Plano e Forma de Pagamento */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                as="select"
                                value={filtroPlanoVendas}
                                onChange={(e) => setFiltroPlanoVendas(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              >
                                <option value="" style={{ color: 'black' }}>Todos os planos</option>
                                {Array.from(new Set(relatorio.vendas.data.map(v => v.plano))).map(plano => (
                                  <option key={plano} value={plano} style={{ color: 'black' }}>{plano}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                as="select"
                                value={filtroFormaPagamentoVendas}
                                onChange={(e) => setFiltroFormaPagamentoVendas(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              >
                                <option value="" style={{ color: 'black' }}>Todas as formas de pagamento</option>
                                {Array.from(new Set(relatorio.vendas.data.map(v => v.forma_pagamento))).map(forma => (
                                  <option key={forma} value={forma} style={{ color: 'black' }}>{formatarFormaPagamento(forma)}</option>
                                ))}
                              </Form.Control>
                            </Col>
                          </Row>
                          {/* Segunda linha: Datas */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                placeholder="Data início"
                                value={filtroDataInicioVendas}
                                onChange={(e) => setFiltroDataInicioVendas(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                placeholder="Data fim"
                                value={filtroDataFimVendas}
                                onChange={(e) => setFiltroDataFimVendas(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              />
                            </Col>
                          </Row>
                          {/* Terceira linha: Botão Limpar */}
                          <Row className="g-2">
                            <Col md={12}>
                              <Button
                                size="sm"
                                variant="outline-light"
                                onClick={() => {
                                  setFiltroPlanoVendas("");
                                  setFiltroFormaPagamentoVendas("");
                                  setFiltroDataInicioVendas("");
                                  setFiltroDataFimVendas("");
                                  setPaginaVendas(1);
                                }}
                                className="w-100"
                              >
                                <i className="fas fa-times"></i> Limpar
                              </Button>
                            </Col>
                          </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                          {relatorio.vendas.data.length > 0 ? (
                            <Table responsive striped hover className="mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th>Plano</th>
                                  <th>Forma de Pagamento</th>
                                  <th>Email</th>
                                  <th>Telefone</th>
                                  <th>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {vendasPaginadas.map((venda, index) => (
                                  <tr key={index}>
                                    <td>
                                      <span className="badge badge-success">{venda.plano}</span>
                                    </td>
                                    <td>{formatarFormaPagamento(venda.forma_pagamento)}</td>
                                    <td>{venda.email}</td>
                                    <td>{formatarTelefone(venda.telefone)}</td>
                                    <td>{formatarData(venda.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-inbox fa-3x mb-3"></i>
                              <p>Nenhuma venda encontrada</p>
                            </div>
                          )}
                          {/* Paginação para Vendas */}
                          {totalPaginasVendas > 1 && (
                            <div className="d-flex justify-content-center mt-3 mb-3">
                              <ButtonGroup>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={paginaVendas === 1}
                                  onClick={() => setPaginaVendas(paginaVendas - 1)}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </Button>
                                <Button variant="success" size="sm" disabled>
                                  {paginaVendas} de {totalPaginasVendas}
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  disabled={paginaVendas === totalPaginasVendas}
                                  onClick={() => setPaginaVendas(paginaVendas + 1)}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </Button>
                              </ButtonGroup>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      {/* Tabela de Desistências */}
                      <Card className="shadow-sm mb-4 h-100">
                        <Card.Header className="bg-warning text-white">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                              <i className="fas fa-times-circle mr-2"></i>
                              Desistências ({relatorio.desistencias.quantidade})
                            </h5>
                            <Button 
                              variant="outline-light" 
                              size="sm" 
                              onClick={exportarDesistenciasXLSX}
                              title="Exportar para Excel"
                            >
                              <i className="fas fa-download mr-1"></i>
                              Exportar XLSX
                            </Button>
                          </div>
                          {/* Filtros da Tabela de Desistências */}
                          {/* Primeira linha: Plano e Forma de Pagamento */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                as="select"
                                value={filtroPlanoDesistencias}
                                onChange={(e) => setFiltroPlanoDesistencias(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              >
                                <option value="" style={{ color: 'black' }}>Todos os planos</option>
                                {Array.from(new Set(relatorio.desistencias.data.map(d => d.plano))).map(plano => (
                                  <option key={plano} value={plano} style={{ color: 'black' }}>{plano}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                as="select"
                                value={filtroFormaPagamentoDesistencias}
                                onChange={(e) => setFiltroFormaPagamentoDesistencias(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              >
                                <option value="" style={{ color: 'black' }}>Todas as formas de pagamento</option>
                                {Array.from(new Set(relatorio.desistencias.data.map(d => d.forma_pagamento))).map(forma => (
                                  <option key={forma} value={forma} style={{ color: 'black' }}>{formatarFormaPagamento(forma)}</option>
                                ))}
                              </Form.Control>
                            </Col>
                          </Row>
                          {/* Segunda linha: Datas */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                placeholder="Data início"
                                value={filtroDataInicioDesistencias}
                                onChange={(e) => setFiltroDataInicioDesistencias(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                placeholder="Data fim"
                                value={filtroDataFimDesistencias}
                                onChange={(e) => setFiltroDataFimDesistencias(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              />
                            </Col>
                          </Row>
                          {/* Terceira linha: Botão Limpar */}
                          <Row className="g-2">
                            <Col md={12}>
                              <Button
                                size="sm"
                                variant="outline-light"
                                onClick={() => {
                                  setFiltroPlanoDesistencias("");
                                  setFiltroFormaPagamentoDesistencias("");
                                  setFiltroDataInicioDesistencias("");
                                  setFiltroDataFimDesistencias("");
                                  setPaginaDesistencias(1);
                                }}
                                className="w-100"
                              >
                                <i className="fas fa-times"></i> Limpar
                              </Button>
                            </Col>
                          </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                          {relatorio.desistencias.data.length > 0 ? (
                            <Table responsive striped hover className="mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th>Plano</th>
                                  <th>Forma de Pagamento</th>
                                  <th>Email</th>
                                  <th>Telefone</th>
                                  <th>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {desistenciasPaginadas.map((desistencia, index) => (
                                  <tr key={index}>
                                    <td>
                                      <span className="badge badge-warning">{desistencia.plano}</span>
                                    </td>
                                    <td>{formatarFormaPagamento(desistencia.forma_pagamento)}</td>
                                    <td>{desistencia.email}</td>
                                    <td>{formatarTelefone(desistencia.telefone)}</td>
                                    <td>{formatarData(desistencia.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-inbox fa-3x mb-3"></i>
                              <p>Nenhuma desistência encontrada</p>
                            </div>
                          )}
                          {/* Paginação para Desistências */}
                          {totalPaginasDesistencias > 1 && (
                            <div className="d-flex justify-content-center mt-3 mb-3">
                              <ButtonGroup>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  disabled={paginaDesistencias === 1}
                                  onClick={() => setPaginaDesistencias(paginaDesistencias - 1)}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </Button>
                                <Button variant="warning" size="sm" disabled>
                                  {paginaDesistencias} de {totalPaginasDesistencias}
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  disabled={paginaDesistencias === totalPaginasDesistencias}
                                  onClick={() => setPaginaDesistencias(paginaDesistencias + 1)}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </Button>
                              </ButtonGroup>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Tabela de Recusados */}
                  <Row className="mb-4">
                    <Col md={12}>
                      <Card className="shadow-sm mb-4">
                        <Card.Header className="bg-danger text-white">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                              <i className="fas fa-ban mr-2"></i>
                              Recusados ({relatorio.recusados.quantidade})
                            </h5>
                            <Button 
                              variant="outline-light" 
                              size="sm" 
                              onClick={exportarRecusadosXLSX}
                              title="Exportar para Excel"
                            >
                              <i className="fas fa-download mr-1"></i>
                              Exportar XLSX
                            </Button>
                          </div>
                          {/* Filtros da Tabela de Recusados */}
                          {/* Primeira linha: Plano e Forma de Pagamento */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                as="select"
                                value={filtroPlanoRecusados}
                                onChange={(e) => setFiltroPlanoRecusados(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              >
                                <option value="" style={{ color: 'black' }}>Todos os planos</option>
                                {Array.from(new Set(relatorio.recusados.data.map(r => r.plano))).map(plano => (
                                  <option key={plano} value={plano} style={{ color: 'black' }}>{plano}</option>
                                ))}
                              </Form.Control>
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                as="select"
                                value={filtroFormaPagamentoRecusados}
                                onChange={(e) => setFiltroFormaPagamentoRecusados(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              >
                                <option value="" style={{ color: 'black' }}>Todas as formas de pagamento</option>
                                {Array.from(new Set(relatorio.recusados.data.map(r => r.forma_pagamento))).map(forma => (
                                  <option key={forma} value={forma} style={{ color: 'black' }}>{formatarFormaPagamento(forma)}</option>
                                ))}
                              </Form.Control>
                            </Col>
                          </Row>
                          {/* Segunda linha: Datas */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                placeholder="Data início"
                                value={filtroDataInicioRecusados}
                                onChange={(e) => setFiltroDataInicioRecusados(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                placeholder="Data fim"
                                value={filtroDataFimRecusados}
                                onChange={(e) => setFiltroDataFimRecusados(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                              />
                            </Col>
                          </Row>
                          {/* Terceira linha: Botão Limpar */}
                          <Row className="g-2">
                            <Col md={12}>
                              <Button
                                size="sm"
                                variant="outline-light"
                                onClick={() => {
                                  setFiltroPlanoRecusados("");
                                  setFiltroFormaPagamentoRecusados("");
                                  setFiltroDataInicioRecusados("");
                                  setFiltroDataFimRecusados("");
                                  setPaginaRecusados(1);
                                }}
                                className="w-100"
                              >
                                <i className="fas fa-times"></i> Limpar
                              </Button>
                            </Col>
                          </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                          {relatorio.recusados.data.length > 0 ? (
                            <Table responsive striped hover className="mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th>Plano</th>
                                  <th>Forma de Pagamento</th>
                                  <th>Email</th>
                                  <th>Telefone</th>
                                  <th>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recusadosPaginadas.map((recusado, index) => (
                                  <tr key={index}>
                                    <td>
                                      <span className="badge badge-danger">{recusado.plano}</span>
                                    </td>
                                    <td>{formatarFormaPagamento(recusado.forma_pagamento)}</td>
                                    <td>{recusado.email}</td>
                                    <td>{formatarTelefone(recusado.telefone)}</td>
                                    <td>{formatarData(recusado.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <div className="text-center py-4 text-muted">
                              <i className="fas fa-inbox fa-3x mb-3"></i>
                              <p>Nenhum pagamento recusado encontrado</p>
                            </div>
                          )}
                          {/* Paginação para Recusados */}
                          {totalPaginasRecusados > 1 && (
                            <div className="d-flex justify-content-center mt-3 mb-3">
                              <ButtonGroup>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={paginaRecusados === 1}
                                  onClick={() => setPaginaRecusados(paginaRecusados - 1)}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </Button>
                                <Button variant="danger" size="sm" disabled>
                                  {paginaRecusados} de {totalPaginasRecusados}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={paginaRecusados === totalPaginasRecusados}
                                  onClick={() => setPaginaRecusados(paginaRecusados + 1)}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </Button>
                              </ButtonGroup>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* Tabelas Lado a Lado: Testaram e Alunos Presenciais */}
                  <Row className="mt-4">
                    {/* Tabela de Testaram */}
                    <Col md={6}>
                      <Card className="shadow-sm mb-4 h-100">
                        <Card.Header className="bg-info text-white">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                              <i className="fas fa-user-check mr-2"></i>
                              Usuários de TESTE cadastrados ({dadosTestaram.length})
                            </h5>
                            <Button 
                              variant="outline-light" 
                              size="sm" 
                              onClick={exportarTestaramXLSX}
                              title="Exportar para Excel"
                            >
                              <i className="fas fa-download mr-1"></i>
                              Exportar XLSX
                            </Button>
                          </div>
                          {/* Filtros da Tabela de Testaram */}
                          <Row className="g-2">
                            <Col md={4}>
                              <Form.Control
                                size="sm"
                                type="date"
                                value={filtroDataInicioTestaram}
                                onChange={(e) => setFiltroDataInicioTestaram(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                                placeholder="Data inicial"
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Control
                                size="sm"
                                type="date"
                                value={filtroDataFimTestaram}
                                onChange={(e) => setFiltroDataFimTestaram(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                                placeholder="Data final"
                              />
                            </Col>
                            <Col md={4}>
                              <Form.Control
                                as="select"
                                size="sm"
                                value={filtroTestou}
                                onChange={(e) => setFiltroTestou(e.target.value)}
                                style={{ 
                                  backgroundColor: 'rgba(255,255,255,0.2)', 
                                  border: '1px solid rgba(255,255,255,0.3)', 
                                  color: 'white' 
                                }}
                              >
                                <option value="" style={{ color: 'black', backgroundColor: 'white' }}>Todos</option>
                                <option value="sim" style={{ color: 'black', backgroundColor: 'white' }}>Enviou uma redação de teste</option>
                                <option value="nao" style={{ color: 'black', backgroundColor: 'white' }}>Não enviou uma redação de teste</option>
                              </Form.Control>
                            </Col>
                          </Row>
                          {/* Botão Limpar */}
                          <Row className="g-2 mt-2">
                            <Col md={12}>
                              <Button
                                size="sm"
                                variant="outline-light"
                                onClick={() => {
                                  setFiltroDataInicioTestaram("");
                                  setFiltroDataFimTestaram("");
                                  setFiltroTestou("");
                                  setPaginaTestaram(1);
                                }}
                                className="w-100"
                              >
                                <i className="fas fa-times"></i> Limpar
                              </Button>
                            </Col>
                          </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                          {loadingTestaram ? (
                            <div className="text-center py-4">
                              <Spinner animation="border" variant="info" />
                              <p className="mt-2 text-muted">Carregando dados...</p>
                            </div>
                          ) : testaramPaginadas.length > 0 ? (
                            <Table striped bordered hover responsive size="sm" className="mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th>Nome</th>
                                  <th>Email</th>
                                  <th>Telefone</th>
                                  <th>Enviou uma redação</th>
                                  <th>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {testaramPaginadas.map((testaram, index) => (
                                  <tr key={index}>
                                    <td>{testaram.nome}</td>
                                    <td>{testaram.email}</td>
                                    <td>{formatarTelefone(testaram.telefone)}</td>
                                    <td>
                                      <span className={`badge ${testaram.testou ? 'badge-success' : 'badge-danger'}`}>
                                        {testaram.testou ? 'Sim' : 'Não'}
                                      </span>
                                    </td>
                                    <td>{formatarData(testaram.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <div className="text-center py-4">
                              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                              <p className="text-muted">Nenhum usuário testou no período selecionado</p>
                            </div>
                          )}
                          {/* Paginação para Testaram */}
                          {totalPaginasTestaram > 1 && (
                            <div className="d-flex justify-content-center mt-3 mb-3">
                              <ButtonGroup>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  disabled={paginaTestaram === 1}
                                  onClick={() => setPaginaTestaram(paginaTestaram - 1)}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </Button>
                                <Button variant="info" size="sm" disabled>
                                  {paginaTestaram} de {totalPaginasTestaram}
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  disabled={paginaTestaram === totalPaginasTestaram}
                                  onClick={() => setPaginaTestaram(paginaTestaram + 1)}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </Button>
                              </ButtonGroup>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* Tabela de Alunos Presenciais */}
                    <Col md={6}>
                      <Card className="shadow-sm mb-4 h-100">
                        <Card.Header className="bg-primary text-white">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">
                              <i className="fas fa-users mr-2"></i>
                              Presenciais ({alunosPresenciais.length})
                            </h5>
                            <Button
                              variant="outline-light"
                              size="sm"
                              onClick={() => exportarPresenciaisXLSX()}
                              disabled={loadingPresenciais}
                            >
                              <i className="fas fa-file-excel mr-1"></i>
                              Exportar XLSX
                            </Button>
                          </div>
                          {/* Filtros para Alunos Presenciais */}
                          <Row className="g-2 mb-2">
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                value={filtroDataInicioPresenciais}
                                onChange={(e) => setFiltroDataInicioPresenciais(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                                placeholder="Data inicial"
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Control
                                size="sm"
                                type="date"
                                value={filtroDataFimPresenciais}
                                onChange={(e) => setFiltroDataFimPresenciais(e.target.value)}
                                style={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                                className="text-white"
                                placeholder="Data final"
                              />
                            </Col>
                          </Row>
                          {/* Botão Limpar */}
                          <Row className="g-2">
                            <Col md={12}>
                              <Button
                                size="sm"
                                variant="outline-light"
                                onClick={() => {
                                  setFiltroDataInicioPresenciais("");
                                  setFiltroDataFimPresenciais("");
                                  setPaginaPresenciais(1);
                                }}
                                className="w-100"
                              >
                                <i className="fas fa-times"></i> Limpar
                              </Button>
                            </Col>
                          </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                          {loadingPresenciais ? (
                            <div className="text-center py-4">
                              <Spinner animation="border" variant="primary" />
                              <p className="mt-2 text-muted">Carregando alunos presenciais...</p>
                            </div>
                          ) : presenciaisFiltradas.length > 0 ? (
                            <Table striped bordered hover responsive size="sm" className="mb-0">
                              <thead className="bg-light">
                                <tr>
                                  <th>Nome</th>
                                  <th>Email</th>
                                  <th>Telefone</th>
                                  <th>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {presenciaisPaginadas.map((aluno, index) => (
                                  <tr key={index}>
                                    <td>{aluno.nome}</td>
                                    <td>{aluno.email}</td>
                                    <td>{formatarTelefone(aluno.telefone)}</td>
                                    <td>{formatarData(aluno.created_at)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          ) : (
                            <div className="text-center py-4">
                              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                              <p className="text-muted">Nenhum aluno presencial encontrado no período selecionado</p>
                            </div>
                          )}
                          {/* Paginação para Alunos Presenciais */}
                          {totalPaginasPresenciais > 1 && (
                            <div className="d-flex justify-content-center mt-3 mb-3">
                              <ButtonGroup>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  disabled={paginaPresenciais === 1}
                                  onClick={() => setPaginaPresenciais(paginaPresenciais - 1)}
                                >
                                  <i className="fas fa-chevron-left"></i>
                                </Button>
                                <Button variant="primary" size="sm" disabled>
                                  {paginaPresenciais} de {totalPaginasPresenciais}
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  disabled={paginaPresenciais === totalPaginasPresenciais}
                                  onClick={() => setPaginaPresenciais(paginaPresenciais + 1)}
                                >
                                  <i className="fas fa-chevron-right"></i>
                                </Button>
                              </ButtonGroup>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </S.MainWrapper>
    </Template>
  );
}

export default Relatorios;